import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { LeadAttributeApiService } from '../services/lead-attribute-api.service';
import { LeadAttributeResponse } from '../models/lead-attribute.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';

type BadgeColor = 'primary' | 'success' | 'error' | 'warning' | 'info' | 'light' | 'dark';

@Component({
  selector: 'app-lead-attribute-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    BadgeComponent,
    HasPermissionDirective
  ],
  templateUrl: './lead-attribute-list.component.html'
})
export class LeadAttributeListComponent implements OnInit {
  atributos: LeadAttributeResponse[] = [];
  loading = false;
  error = '';

  constructor(
    private attributeApi: LeadAttributeApiService,
    private authService: AuthService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    this.cargarAtributos();
  }

  cargarAtributos(): void {
    this.loading = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.attributeApi.getAttributes(businessId).subscribe({
      next: (data) => {
        this.atributos = data;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar los atributos de lead';
        this.loading = false;
        console.error('Error:', err);
      }
    });
  }

  nuevoAtributo(): void {
    this.router.navigate(['/config-leads/atributos/crear']);
  }

  editarAtributo(atributo: LeadAttributeResponse): void {
    this.router.navigate(['/config-leads/atributos/editar', atributo.id]);
  }

  async eliminarAtributo(atributo: LeadAttributeResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar atributo',
      message: `¿Está seguro de eliminar el atributo "${atributo.displayName}"?`
    });
    if (!ok) return;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.attributeApi.deleteAttribute(atributo.id, businessId).subscribe({
      next: () => {
        this.cargarAtributos();
        this.toast.deleted('Atributo de lead', `Se eliminó "${atributo.displayName}" correctamente.`);
      },
      error: (err) => {
        this.confirmDialog.alert({ title: 'Error', message: err.error?.message || 'Error al eliminar el atributo', variant: 'danger' });
      }
    });
  }

  getActiveColor(isActive: boolean): BadgeColor {
    return isActive ? 'success' : 'error';
  }

  getActiveLabel(isActive: boolean): string {
    return isActive ? 'Activo' : 'Inactivo';
  }

  getValueTypeLabel(valueType: string): string {
    const labels: Record<string, string> = {
      'STRING': 'Texto',
      'NUMBER': 'Número',
      'BOOLEAN': 'Booleano',
      'DATE': 'Fecha',
      'FK': 'Referencia'
    };
    return labels[valueType] || valueType;
  }

  getValueTypeColor(valueType: string): BadgeColor {
    const colors: Record<string, BadgeColor> = {
      'STRING': 'info',
      'NUMBER': 'primary',
      'BOOLEAN': 'warning',
      'DATE': 'light',
      'FK': 'dark'
    };
    return colors[valueType] || 'light';
  }
}
